import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { AbstractDialogData, DialogComponent } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { toLocaleSignal } from 'src/app/shared/utils/locale.utils';
import {
  FieldObservationDialogDto,
  FieldObservationImageDialogDto,
  FieldObservationSectionDto,
} from '../../../shared/models/models';

export interface FieldObservationDialogData extends AbstractDialogData {
  cropFieldName?: string;
  fieldObservations: FieldObservationDialogDto[];
  initialObservationIndex?: number;
}

interface FieldObservationViewModel {
  observationDate: string;
  cropFieldName: string;
  remarks: string | null;
  images: FieldObservationImageDialogDto[];
  observations: FieldObservationSectionDto[];
}

@Component({
  selector: 'app-field-observation-dialog',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet, MatIconButton, MatIcon, MatTabGroup, MatTab, TranslatePipe, DialogComponent],
  templateUrl: './field-observation-dialog.component.html',
  styleUrl: './field-observation-dialog.component.scss',
})
export class FieldObservationDialogComponent {
  private static readonly COMPACT_LAYOUT_BREAKPOINT = 960;
  readonly data = inject<FieldObservationDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<FieldObservationDialogComponent>);
  private readonly localeService = inject(LocaleService);
  private imageElementRef?: ElementRef<HTMLImageElement>;
  private readonly maxZoomLevel = 3;
  private activePointerId: number | null = null;
  private activePointerTarget: HTMLElement | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOriginX = 0;
  private dragOriginY = 0;

  @ViewChild('imageStage') private readonly imageStageRef?: ElementRef<HTMLDivElement>;
  @ViewChild('mainImage')
  private set mainImageRef(value: ElementRef<HTMLImageElement> | undefined) {
    this.imageElementRef = value;
  }

  protected readonly locale = toLocaleSignal(this.localeService);
  protected readonly observationDays = computed(() => this.normalizeObservationDays());

  protected readonly currentObservationIndex = signal(this.data.initialObservationIndex ?? 0);
  protected readonly currentImageIndex = signal(0);
  protected readonly selectedMobileTab = signal(0);
  protected readonly isMobileImageControlsOpen = signal(false);
  protected readonly zoomLevel = signal(1);
  protected readonly panX = signal(0);
  protected readonly panY = signal(0);
  protected readonly isCompactLayout = signal(this.getIsCompactLayout());
  protected readonly isZoomedIn = computed(() => this.zoomLevel() > 1);
  protected readonly canZoomIn = computed(() => this.zoomLevel() < this.maxZoomLevel);
  protected readonly canZoomOut = computed(() => this.zoomLevel() > 1);
  protected readonly showNextImageButton = computed(() => !this.isCompactLayout() || !this.isMobileImageControlsOpen());
  protected readonly imageTransform = computed(
    () => `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomLevel()})`,
  );

  protected readonly currentObservation = computed(
    (): FieldObservationViewModel =>
      this.observationDays()[this.currentObservationIndex()] ?? this.observationDays()[0],
  );
  protected readonly currentImages = computed(() => this.currentObservation().images);
  protected readonly hasCurrentImages = computed(() => this.currentImages().length > 0);
  protected readonly currentImage = computed(() => this.currentImages()[this.currentImageIndex()] ?? null);
  protected readonly currentImageSrc = computed(() => {
    const image = this.currentImage();

    if (!image) {
      return null;
    }

    if (image.imageUrl) {
      return image.imageUrl;
    }

    if (image.imageBase64) {
      return `data:${image.contentType ?? 'image/jpeg'};base64,${image.imageBase64}`;
    }

    return null;
  });
  protected readonly canGoToPreviousObservation = computed(() => this.currentObservationIndex() > 0);
  protected readonly canGoToNextObservation = computed(
    () => this.currentObservationIndex() < this.observationDays().length - 1,
  );
  protected readonly canGoToPreviousImage = computed(() => this.currentImageIndex() > 0);
  protected readonly canGoToNextImage = computed(() => this.currentImageIndex() < this.currentImages().length - 1);
  protected readonly imageCounter = computed(() => `${this.currentImageIndex() + 1} / ${this.currentImages().length}`);
  protected readonly currentAltText = computed(() => this.imageAlt(this.currentImage()));
  protected readonly observationCountTranslationParams = computed(() => ({
    current: String(this.currentObservationIndex() + 1),
    total: String(this.observationDays().length),
  }));
  protected isDraggingImage = false;

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.isCompactLayout.set(this.getIsCompactLayout());
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected onPreviousObservation(): void {
    if (!this.canGoToPreviousObservation()) {
      return;
    }

    this.currentObservationIndex.update((index) => index - 1);
    this.resetImageState();
  }

  protected onNextObservation(): void {
    if (!this.canGoToNextObservation()) {
      return;
    }

    this.currentObservationIndex.update((index) => index + 1);
    this.resetImageState();
  }

  protected onPreviousImage(): void {
    if (!this.canGoToPreviousImage()) {
      return;
    }

    this.currentImageIndex.update((index) => index - 1);
    this.resetZoomAndPan();
  }

  protected onNextImage(): void {
    if (!this.canGoToNextImage()) {
      return;
    }

    this.currentImageIndex.update((index) => index + 1);
    this.resetZoomAndPan();
  }

  protected onSelectImage(index: number): void {
    this.currentImageIndex.set(index);
    this.resetZoomAndPan();
  }

  protected onImageWheel(event: WheelEvent): void {
    if (!this.currentImageSrc()) {
      return;
    }

    event.preventDefault();

    if (event.deltaY < 0) {
      this.onZoomIn();
      return;
    }

    this.onZoomOut();
  }

  protected onZoomIn(): void {
    this.setZoomLevel(this.zoomLevel() + 0.25);
  }

  protected onZoomOut(): void {
    this.setZoomLevel(this.zoomLevel() - 0.25);
  }

  protected onResetZoom(): void {
    this.resetZoomAndPan();
  }

  protected onMobileTabChange(index: number): void {
    this.selectedMobileTab.set(index);
  }

  protected toggleMobileImageControls(): void {
    this.isMobileImageControlsOpen.update((isOpen) => !isOpen);
  }

  protected onImagePointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !this.isZoomedIn()) {
      return;
    }

    const currentTarget = event.currentTarget;
    if (!(currentTarget instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    currentTarget.setPointerCapture(event.pointerId);

    this.activePointerId = event.pointerId;
    this.activePointerTarget = currentTarget;
    this.isDraggingImage = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragOriginX = this.panX();
    this.dragOriginY = this.panY();
  }

  protected onImagePointerMove(event: PointerEvent): void {
    if (!this.isDraggingImage || this.activePointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;
    const clampedPosition = this.clampPanPosition(this.dragOriginX + deltaX, this.dragOriginY + deltaY);

    this.panX.set(clampedPosition.x);
    this.panY.set(clampedPosition.y);
  }

  protected onImagePointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }

    if (this.activePointerTarget?.hasPointerCapture(event.pointerId)) {
      this.activePointerTarget.releasePointerCapture(event.pointerId);
    }

    this.stopDraggingImage();
  }

  protected onImageLoad(): void {
    this.clampCurrentPan();
  }

  protected imageTrackKey(image: FieldObservationImageDialogDto, index: number): string {
    return (
      image.imageUrl ??
      image.thumbnailUrl ??
      image.imageBase64 ??
      image.thumbnailBase64 ??
      image.alt ??
      `image-${index}`
    );
  }

  protected selectImageTranslationParams(index: number): { index: string } {
    return { index: String(index + 1) };
  }

  protected thumbnailSrc(image: FieldObservationImageDialogDto): string | null {
    if (image.thumbnailUrl) {
      return image.thumbnailUrl;
    }

    if (image.thumbnailBase64) {
      return `data:${image.contentType ?? 'image/jpeg'};base64,${image.thumbnailBase64}`;
    }

    if (image.imageUrl) {
      return image.imageUrl;
    }

    if (image.imageBase64) {
      return `data:${image.contentType ?? 'image/jpeg'};base64,${image.imageBase64}`;
    }

    return null;
  }

  protected observationIcon(category: string): string {
    const normalizedCategory = category.trim().toLowerCase();

    if (normalizedCategory.endsWith('.fields.bbch')) {
      return 'tag';
    }

    if (normalizedCategory.endsWith('.fields.cropphase')) {
      return 'eco';
    }

    if (normalizedCategory.endsWith('.fields.phytophthora')) {
      return 'opacity';
    }

    if (normalizedCategory.endsWith('.fungi')) {
      return 'coronavirus';
    }

    if (normalizedCategory.endsWith('.insects')) {
      return 'pest_control';
    }

    if (normalizedCategory.endsWith('.weeds')) {
      return 'grass';
    }

    if (normalizedCategory.endsWith('.bacteria')) {
      return 'biotech';
    }

    if (normalizedCategory.endsWith('.nutrientdeficiencies')) {
      return 'agriculture';
    }

    if (normalizedCategory.endsWith('.physicaldefects')) {
      return 'crop';
    }

    return 'label';
  }

  protected shouldTranslateObservationItems(category: string): boolean {
    return category !== 'TGA.Notifications.FieldObservationDialog.Fields.Bbch';
  }

  protected imageAlt(image: FieldObservationImageDialogDto | null): string {
    const altText = image?.alt
      ?.replaceAll(/\bimage\b/gi, '')
      .replaceAll(/\s{2,}/g, ' ')
      .trim();

    return altText || this.currentObservation().cropFieldName;
  }

  private setZoomLevel(nextZoomLevel: number): void {
    const clampedZoomLevel = Math.max(1, Math.min(nextZoomLevel, this.maxZoomLevel));

    this.zoomLevel.set(clampedZoomLevel);

    if (clampedZoomLevel === 1) {
      this.resetPan();
      return;
    }

    this.clampCurrentPan();
  }

  private clampCurrentPan(): void {
    const clampedPosition = this.clampPanPosition(this.panX(), this.panY());
    this.panX.set(clampedPosition.x);
    this.panY.set(clampedPosition.y);
  }

  private clampPanPosition(x: number, y: number): { x: number; y: number } {
    const { maxOffsetX, maxOffsetY } = this.getPanBounds();

    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, x)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, y)),
    };
  }

  private getPanBounds(): { maxOffsetX: number; maxOffsetY: number } {
    const imageStage = this.imageStageRef?.nativeElement;
    const image = this.imageElementRef?.nativeElement;

    if (!imageStage || !image) {
      return { maxOffsetX: 0, maxOffsetY: 0 };
    }

    const computedStyle = globalThis.getComputedStyle(imageStage);
    const paddingX = Number.parseFloat(computedStyle.paddingLeft) + Number.parseFloat(computedStyle.paddingRight);
    const paddingY = Number.parseFloat(computedStyle.paddingTop) + Number.parseFloat(computedStyle.paddingBottom);
    const availableWidth = Math.max(0, imageStage.clientWidth - paddingX);
    const availableHeight = Math.max(0, imageStage.clientHeight - paddingY);
    const scaledImageWidth = image.clientWidth * this.zoomLevel();
    const scaledImageHeight = image.clientHeight * this.zoomLevel();

    return {
      maxOffsetX: Math.max(0, (scaledImageWidth - availableWidth) / 2),
      maxOffsetY: Math.max(0, (scaledImageHeight - availableHeight) / 2),
    };
  }

  private resetPan(): void {
    this.stopDraggingImage();
    this.panX.set(0);
    this.panY.set(0);
  }

  private stopDraggingImage(): void {
    this.isDraggingImage = false;
    this.activePointerId = null;
    this.activePointerTarget = null;
  }

  private resetImageState(): void {
    this.currentImageIndex.set(0);
    this.resetZoomAndPan();
  }

  private resetZoomAndPan(): void {
    this.resetPan();
    this.zoomLevel.set(1);
    this.isMobileImageControlsOpen.set(false);
  }

  private getIsCompactLayout(): boolean {
    if (typeof globalThis.innerWidth !== 'number') {
      return false;
    }

    return globalThis.innerWidth <= FieldObservationDialogComponent.COMPACT_LAYOUT_BREAKPOINT;
  }

  private normalizeObservationDays(): FieldObservationViewModel[] {
    const fieldObservations = this.data.fieldObservations.length
      ? this.data.fieldObservations
      : [this.createEmptyObservation()];

    return fieldObservations.map((fieldObservation) => this.toViewModel(fieldObservation));
  }

  private createEmptyObservation(): FieldObservationDialogDto {
    return {
      cropFieldName: this.data.cropFieldName,
      images: [],
      observations: [],
    };
  }

  private toViewModel(fieldObservation: FieldObservationDialogDto): FieldObservationViewModel {
    return {
      observationDate: fieldObservation.observationDate ?? '',
      cropFieldName: fieldObservation.cropFieldName ?? this.data.cropFieldName ?? '',
      remarks: fieldObservation.remarks ?? null,
      images: fieldObservation.images ?? [],
      observations: fieldObservation.observations ?? [],
    };
  }
}
